import * as React from "react";

import {isTruthy} from "@khanacademy/wonder-stuff-core";
import ErrorLoading from "@khan/component-screens/error-loading";
import {doNotTranslateYet} from "@khan/i18n";
import {useLocation} from "@khan/urls";

import NotFound from "#app-shell-package/errors/not-found.tsx";
import PageRoot from "#app-shell-package/page-root.tsx";
import {FlexPageTemplate} from "#app-shell-package/page-templates/flex-page-template.tsx";

import type {StandardRegionPresentationsQuery_standardRegions as StandardRegionWithError} from "./__graphql-types__/StandardRegionPresentationsQuery.ts";
import CenteredCircularSpinner from "./centered-circular-spinner.tsx";
import {countriesBySlug} from "./countries-by-slug.js";
import {STANDARD_US_REGION_CODE_TO_NAME} from "./region-name-by-code.ts";
import {RegionalStandardsPresentationContent} from "./regional-standards-presentation-content.tsx";
import type {StandardRegion} from "./types.ts";
import {useStandardRegionPresentationsQuery} from "./use-standard-region-presentations-query.ts";

const mapFirstStandardRegion = (
    standardRegionsResult?: Array<StandardRegionWithError> | null,
): StandardRegion | null | undefined => {
    const regions = (standardRegionsResult ?? [])
        .filter(isTruthy)
        .filter(({standardRegion}) => !!standardRegion);
    if (!regions.length) {
        return null;
    }
    const region = regions[0].standardRegion;
    return {
        ...region,
        name:
            STANDARD_US_REGION_CODE_TO_NAME.find(
                ({code}) => code === region?.id,
            )?.name || region?.id,
    } as StandardRegion;
};

type Props = {
    countrySlug: string;
    regionSlug: string;
};

const RegionalStandardsPage = (props: Props): React.ReactElement => {
    const {countrySlug, regionSlug} = props;
    const country = countriesBySlug[countrySlug];

    // For testing, ?discoverable forces this StandardRegion to not render the
    // <NotFound /> page regardless of its isDiscoverable value. It also forces
    // all SetOfStandards for this StandardRegion to be rendered regardless of
    // each set's isDiscoverable value.
    const {search, pathname} = useLocation();
    const overrideDiscoverable = new URLSearchParams(search).has(
        "discoverable",
    );

    const standardRegionPresentationsResult =
        useStandardRegionPresentationsQuery(
            countrySlug,
            regionSlug,
            `${countrySlug}/${regionSlug}`,
            overrideDiscoverable ? undefined : true,
        );

    if (country === undefined) {
        return <NotFound />;
    }

    if (
        standardRegionPresentationsResult.status === "loading" ||
        standardRegionPresentationsResult.status === "no-data"
    ) {
        return (
            <PageRoot
                template={FlexPageTemplate}
                settings={{
                    title: doNotTranslateYet`State Standards`,
                    canonicalUrl: pathname,
                }}
            >
                <CenteredCircularSpinner />
            </PageRoot>
        );
    }

    if (standardRegionPresentationsResult.status !== "success") {
        return <ErrorLoading />;
    }

    const region = mapFirstStandardRegion(
        standardRegionPresentationsResult.data.standardRegions,
    );

    if (!region) {
        return <NotFound />;
    }

    const presentationSections =
        standardRegionPresentationsResult.data.standardRegionPresentations
            .presentationSections;

    const pageTitle = doNotTranslateYet`State Standards - ${region.name} - ${country.name}`;

    return (
        <PageRoot
            settings={{
                noFollow: !region.isDiscoverable,
                noIndex: !region.isDiscoverable,
                title: pageTitle,
                canonicalUrl: pathname,
            }}
        >
            <RegionalStandardsPresentationContent
                standardRegion={region}
                presentationSections={presentationSections}
            />
        </PageRoot>
    );
};

export default RegionalStandardsPage;
